// ─── Kallesh AI Knowledge Base ─────────────────────────────────────────────
// Rule-based NLP chatbot that only answers about Kallesh D R's profile

const KB = {
  name: "Kallesh D R",
  role: "Software Development Engineer",
  experience: "2+ years",
  location: "Bengaluru, Karnataka, India",
  phone: "+91 7676654809",
  email: "kallesh953@gmail.com",
  linkedin: "https://www.linkedin.com/in/kallesh-dr-087008235",
  github: "https://github.com/kalleshdr",

  summary:
    "Kallesh D R is a Software Development Engineer with 2+ years of experience in full-stack development, AI/ML integration, mobile (Android & iOS), and enterprise applications. He has shipped production apps to Google Play used by companies like Raymond, Nuzivedu Seeds, and Priya Cement.",

  skills: {
    languages: ["Python", "Java", "JavaScript", "TypeScript", "C", "C++", "PHP", "Bash"],
    frontend: ["React.js", "React Native", "Flutter", "HTML/CSS", "Bootstrap"],
    backend: ["Node.js", "Flask", "Django", ".NET REST APIs", "RESTful APIs", "Microservices", "MVC"],
    aiml: ["TensorFlow", "PyTorch", "Generative AI", "Predictive Analytics", "Computer Vision", "Machine Learning"],
    databases: ["MySQL", "MongoDB", "PostgreSQL", "SQLite", "Firebase"],
    cloud: ["AWS S3", "AWS EC2", "AWS RDS", "Microsoft Azure", "Firebase", "CI/CD Pipelines"],
    tools: ["Git", "VS Code", "Android Studio", "Eclipse", "XAMPP", "Linux"],
    specializations: ["Full Stack Development", "Cybersecurity", "EdTech Solutions", "System Design", "SEO Optimization"],
  },

  experience_list: [
    {
      company: "Appstrail Technology",
      role: "Associate Consultant Trainee",
      period: "October 2025 – Present",
      highlights: ["React Native Mobile App Developer", "Offline-first Salesforce SFA Apps", "Salesforce SDK Development"],
    },
    {
      company: "Scontinent Technologies Pvt Ltd",
      role: "Software Development Engineer",
      period: "June 2024 – October 2025",
      highlights: ["EdTech platform (React.js, Node.js, Firebase)", "GenAI-powered apps", "NammaCET ML platform for 10,000+ students"],
    },
    {
      company: "Aitch Nu Global Technologies Pvt Ltd",
      role: "Lead & Software Developer Executive",
      period: "May 2024 – April 2025",
      highlights: ["Led team of developers on 10+ projects", "Java, MERN Stack, PHP, Python", "Healthcare & e-commerce apps"],
    },
    {
      company: "ICMR – NCDIR",
      role: "R&D Intern",
      period: "February 2024 – April 2024",
      highlights: ["Data pipelines in Python", "ML for medical research datasets"],
    },
  ],

  projects: [
    { name: "RutX SFA App", stack: "React Native, Salesforce, REST APIs", status: "Live on Google Play", clients: "Raymond, Nuzivedu Seeds, Priya Cement, VNR Seeds" },
    { name: "SDK App", stack: "React Native, Salesforce", status: "Enterprise SDK", desc: "Reusable Salesforce integration SDK" },
    { name: "Samai App", stack: "Python, Generative AI, REST APIs", status: "Live on Google Play", desc: "GenAI-powered Android app" },
    { name: "NammaCET", stack: "Python, ML, React.js", status: "Live at nammacet.com", desc: "KCET rank predictor for 10,000+ students" },
    { name: "ConnectStudents", stack: "Full Stack", status: "Live at connectstudents.in", desc: "Student networking platform, Product for Society" },
    { name: "DQEH SDK", stack: "Python", status: "Published on PyPI & npm", desc: "Quantum encryption hasher for cybersecurity" },
  ],

  education: "B.E. Computer Science, Brindavan College of Engineering (VTU), CGPA 8.58/10, 2020–2024",
  awards: [
    "Best Outgoing Student CSE 2024 – awarded by Anurag Thakur, MLA of Lok Sabha",
    "National 2nd Runner-Up – Pitch It UP! 2.0, MSCW Delhi University",
    "National 2nd Runner-Up – Idea Ignite, MNIT Jaipur University",
    "State 1st Prize – Project Expo, Brindavan College of Engineering",
    "Round-1 Qualifier – URSC-ISRO Robotics Challenge 2024",
  ],
  certifications: [
    "Data Science & Analytics – KSCST",
    "Machine Learning using Python – KSCST",
    "AI & ML Full Course – Simplilearn",
    "Python Programming – IIT Madras",
    "Certified Cyber Forensics Professional (CCFP)",
    "AI For India 2.0 – GUVI / Google",
  ],
  publications: [
    "Engineering the Future: Cutting-Edge Phone Technology with Water, Heat, and Velocity Resistance",
    "Resurrecting Shadows: Overcoming Power-Off Boundaries With GPS-Based Tracking For Switched-Off Smartphones",
  ],
  availability: "Open to full-time SDE roles, freelance projects, consulting, and collaborations. Typically responds within 24 hours.",
  salary: "Available on request. Open to negotiation based on role and scope.",
};

// ─── Response Generator ─────────────────────────────────────────────────────

function match(input, keywords) {
  const lower = input.toLowerCase();
  return keywords.some((k) => lower.includes(k));
}

export function getBotResponse(userMessage, visitorName) {
  const msg = userMessage.toLowerCase().trim();
  const greet = visitorName ? `Hi ${visitorName}! ` : "";

  // --- Greetings ---
  if (match(msg, ["hello", "hi", "hey", "hii", "hola", "good morning", "good afternoon", "good evening", "namaste"])) {
    return `${greet}Hello! I'm AURA, Kallesh's AI assistant. I can answer anything about his profile — skills, experience, projects, education, availability, and more. What would you like to know?`;
  }

  // --- Who is / About ---
  if (match(msg, ["who is kallesh", "about kallesh", "tell me about", "introduce", "summary", "overview"])) {
    return `${KB.summary}\n\nHe's currently at **Appstrail Technology** as an Associate Consultant Trainee. Want to know about his skills, projects, or availability?`;
  }

  // --- Skills ---
  if (match(msg, ["skill", "tech", "stack", "language", "framework", "know", "expertise", "proficient", "tools"])) {
    return `Kallesh's tech stack spans:\n\n• **Languages**: ${KB.skills.languages.join(", ")}\n• **Frontend/Mobile**: ${KB.skills.frontend.join(", ")}\n• **Backend**: ${KB.skills.backend.join(", ")}\n• **AI/ML**: ${KB.skills.aiml.join(", ")}\n• **Databases**: ${KB.skills.databases.join(", ")}\n• **Cloud/DevOps**: ${KB.skills.cloud.join(", ")}\n\nHis specializations include: ${KB.skills.specializations.join(", ")}.`;
  }

  // --- AI / ML specific ---
  if (match(msg, ["ai", "ml", "machine learning", "artificial intelligence", "deep learning", "tensorflow", "pytorch", "generative", "genai", "nlp", "computer vision"])) {
    return `Kallesh has strong AI/ML expertise — he's worked with **TensorFlow, PyTorch, Generative AI, Computer Vision, and Predictive Analytics**.\n\nNotably:\n• Built **Samai**, a live GenAI Android app on Google Play\n• Developed ML-based rank prediction for NammaCET serving 10,000+ students\n• Integrated AI-driven features at Scontinent Technologies`;
  }

  // --- Mobile / React Native / Android ---
  if (match(msg, ["mobile", "android", "ios", "react native", "flutter", "app development", "app dev", "play store"])) {
    return `Yes! Kallesh builds production mobile apps:\n\n• **RutX** (React Native + Salesforce) — Live on Google Play, used by Raymond, Nuzivedu Seeds, Priya Cement, VNR Seeds\n• **Samai** (GenAI Android App) — Live on Google Play, active user base\n• Also experienced with **Flutter** and **Android Studio**\n\nNeed app development? I can collect your requirements — just say "contact" or "hire"!`;
  }

  // --- Experience / Work history ---
  if (match(msg, ["experience", "work", "job", "company", "employer", "career", "worked", "history"])) {
    const list = KB.experience_list.map((e) => `• **${e.role}** at ${e.company} (${e.period})`).join("\n");
    return `Kallesh has **${KB.experience}** of professional experience:\n\n${list}\n\nWant details on any specific role?`;
  }

  // --- Current job ---
  if (match(msg, ["current", "now", "present", "appstrail", "latest"])) {
    const curr = KB.experience_list[0];
    return `Currently, Kallesh is an **${curr.role}** at **${curr.company}** (${curr.period}), working on enterprise software consulting, Python automation, and CI/CD pipelines.`;
  }

  // --- Projects ---
  if (match(msg, ["project", "portfolio", "built", "created", "developed", "product", "app", "platform", "nammacet", "rutx", "samai", "connect", "dqeh", "sdk"])) {
    const list = KB.projects.map((p) => `• **${p.name}** — ${p.stack} | ${p.status}`).join("\n");
    return `Kallesh has shipped ${KB.projects.length} key projects:\n\n${list}\n\nAll are live, production-grade products. Want details on any specific one?`;
  }

  // --- Education ---
  if (match(msg, ["education", "degree", "college", "university", "study", "studied", "vtu", "brindavan", "cgpa", "gpa", "academic"])) {
    return `Kallesh holds a **B.E. in Computer Science** from **Brindavan College of Engineering (VTU)**, Bengaluru.\n\n• Graduated: August 2024\n• **CGPA: 8.58 / 10**\n• Awarded **Best Outgoing Student of CSE 2024** by Member of Lok Sabha Anurag Thakur`;
  }

  // --- Awards / Achievements ---
  if (match(msg, ["award", "achievement", "recognition", "winner", "prize", "competition", "accolades", "isro", "mnit", "mscw"])) {
    const list = KB.awards.map((a) => `• ${a}`).join("\n");
    return `Kallesh has impressive national-level recognition:\n\n${list}`;
  }

  // --- Certifications ---
  if (match(msg, ["certification", "certificate", "certified", "course", "iit", "simplilearn", "guvi", "kscst"])) {
    const list = KB.certifications.map((c) => `• ${c}`).join("\n");
    return `Kallesh holds these certifications:\n\n${list}`;
  }

  // --- Publications / Research ---
  if (match(msg, ["publication", "research", "paper", "published", "journal"])) {
    const list = KB.publications.map((p) => `• "${p}"`).join("\n");
    return `Kallesh has published research papers:\n\n${list}`;
  }

  // --- Contact / Hire / Freelance / Project inquiry ---
  if (match(msg, ["hire", "hiring", "recruit", "job offer", "opportunity", "interested", "contact", "reach", "freelance", "collaborate", "work with", "project", "need you", "requirement"])) {
    return `I'm glad you're interested in working with Kallesh!\n\nPlease use the form below to send your enquiry — I'll collect your details, purpose, and requirements, then Kallesh will follow up within **24 hours**. Let's proceed!`;
  }

  // --- Salary / Rate ---
  if (match(msg, ["salary", "ctc", "rate", "package", "cost", "fee", "price", "charge", "budget"])) {
    return `${KB.salary} For specific rates or packages, please reach out directly — use "contact" to send an enquiry.`;
  }

  // --- Availability ---
  if (match(msg, ["available", "availability", "free", "open to", "notice period", "join", "start", "when can"])) {
    return `${KB.availability}\n\nFeel free to use the enquiry form to reach out — just say "contact"!`;
  }

  // --- Location / Remote ---
  if (match(msg, ["location", "where", "based", "bengaluru", "bangalore", "remote", "onsite", "relocate"])) {
    return `Kallesh is based in **Bengaluru, Karnataka, India**. He's open to both onsite and remote opportunities across India and globally.`;
  }

  // --- Phone / Email / Social ---
  if (match(msg, ["phone", "number", "call", "whatsapp", "mobile number"])) {
    return `You can reach Kallesh at **+91 7676654809**.`;
  }
  if (match(msg, ["email", "mail", "gmail"])) {
    return `Kallesh's email is **kallesh953@gmail.com**.`;
  }
  if (match(msg, ["linkedin", "profile link", "github"])) {
    return `• **LinkedIn**: linkedin.com/in/kallesh-dr-087008235\n• **GitHub**: github.com/kalleshdr`;
  }

  // --- Full Stack / Web Dev ---
  if (match(msg, ["full stack", "web dev", "react", "node", "frontend", "backend", "mern"])) {
    return `Kallesh is a full-stack engineer proficient in the **MERN stack** (MongoDB, Express, React.js, Node.js), along with Python backends (Flask/Django) and RESTful API design. He's delivered EdTech platforms, enterprise tools, and public-facing web products.`;
  }

  // --- Python ---
  if (match(msg, ["python", "flask", "django", "scripting", "automation"])) {
    return `Python is one of Kallesh's strongest languages. He uses it for:\n• Backend APIs (Flask, Django)\n• ML/AI pipelines (TensorFlow, PyTorch)\n• Shell automation & CI/CD scripting\n• Data analytics and research tools`;
  }

  // --- Cloud / DevOps ---
  if (match(msg, ["cloud", "aws", "azure", "devops", "ci/cd", "deployment", "docker", "linux"])) {
    return `Kallesh works with cloud infrastructure including **AWS (S3, EC2, RDS)** and **Microsoft Azure**, with hands-on CI/CD pipeline setup and shell-based automation on Linux environments.`;
  }

  // --- Cybersecurity ---
  if (match(msg, ["security", "cyber", "cybersecurity", "encryption", "hashing", "forensics", "ccfp"])) {
    return `Kallesh holds a **Certified Cyber Forensics Professional (CCFP)** certification and developed **DQEH**, a Python/npm SDK implementing advanced quantum encryption and hashing techniques, published on both PyPI and npm.`;
  }

  // --- Startup / Entrepreneurship ---
  if (match(msg, ["startup", "entrepreneur", "founder", "product", "venture", "business"])) {
    return `Kallesh has built and launched independent products:\n• **ConnectStudents.in** — recognized as a "Product for Society"\n• **DQEH SDK** — published on PyPI & npm\n• Multiple EdTech and enterprise platforms\n\nHe brings both an engineering and product mindset to his work.`;
  }

  // --- Thank you ---
  if (match(msg, ["thank", "thanks", "appreciate", "great", "awesome", "cool", "nice", "good"])) {
    return `You're welcome! Is there anything else you'd like to know about Kallesh? If you're ready to connect, just say "contact" and I'll open the enquiry form.`;
  }

  // --- Goodbye ---
  if (match(msg, ["bye", "goodbye", "see you", "later", "cya", "take care"])) {
    return `Goodbye! Feel free to come back anytime. You can also reach Kallesh directly at kallesh953@gmail.com. Have a great day!`;
  }

  // --- Default fallback ---
  return `I can only answer questions about **Kallesh D R's** profile — his skills, experience, projects, education, achievements, or availability.\n\nTry asking:\n• "What are his skills?"\n• "Tell me about his projects"\n• "Is he available for freelance?"\n• "How can I hire him?"`;
}
